import { toast, useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { Attraction } from '@/config/objects/attractions';

interface TourAttractionsResponse {
  success: boolean;
  data: Attraction[];
}

const buildOrderQueryKey = (tourId: string) =>
  ['TourAttractionOrder', tourId] as const;

/**
 * Fetch a tour's attractions sorted by their tour-defined order
 * (`tourAttraction.order` ascending). Returns up to 500 attractions —
 * tours with more than that don't currently exist and the reorder
 * endpoint also caps at 200.
 */
export function useTourAttractions(tourId: string | null | undefined) {
  const apiClient = useApiClient();

  return useQuery<TourAttractionsResponse>({
    queryKey: buildOrderQueryKey(tourId ?? ''),
    enabled: Boolean(tourId),
    queryFn: async () => {
      const response = await apiClient.get<TourAttractionsResponse>(
        `/tours/${tourId}/attractions?limit=500`,
      );
      // Sort client-side by tourAttraction.order so the dnd-kit list and the
      // backend agree on position even when the API was sorted by something
      // else (default name sort).
      const sorted = [...(response.data ?? [])].sort((a, b) => {
        const aOrder = a.tourAttraction?.order ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.tourAttraction?.order ?? Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });
      return { ...response, data: sorted };
    },
  });
}

/**
 * Replace a tour's attraction order with the given ordered list of attraction
 * ids. Optimistically updates the cached list so the dragged row stays in
 * place while the request is in flight.
 */
export function useUpdateTourAttractionOrder(tourId: string) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = buildOrderQueryKey(tourId);

  return useMutation<
    void,
    Error,
    { attractionIds: string[]; optimisticAttractions?: Attraction[] }
  >({
    mutationFn: async ({ attractionIds }) => {
      await apiClient.put<{ success: boolean }>(
        `/tours/${tourId}/attractions/order`,
        { attractionIds },
      );
    },
    onMutate: async ({ optimisticAttractions }) => {
      if (!optimisticAttractions) return;
      await queryClient.cancelQueries({ queryKey });
      const previous =
        queryClient.getQueryData<TourAttractionsResponse>(queryKey);
      queryClient.setQueryData<TourAttractionsResponse>(queryKey, {
        success: true,
        data: optimisticAttractions,
      });
      return { previous } as unknown as void;
    },
    onError: (error, _vars, context) => {
      const ctx = context as unknown as { previous?: TourAttractionsResponse };
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
      toast.error(error.message || 'Failed to update attraction order');
      // Failure path only: refetch so the cache converges on server truth.
      queryClient.invalidateQueries({ queryKey });
    },
    // No onSettled invalidation — the optimistic update already mirrors
    // what the server now stores. Refetching here would replace every
    // attraction object with a fresh reference, which makes dnd-kit reset
    // its drop transforms mid-animation and produces a visible flash.
    // The neighbouring caches (CrudListView, Tour single) only need to
    // know about new ordering when the user navigates back to them, so
    // we invalidate those without a refetch hint.
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['Attraction'],
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: ['Tour', tourId],
        refetchType: 'none',
      });
    },
  });
}
